import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loadsAPI, loadRequestsAPI, notificationsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { VerificationBadge, LoadStatusBadge, RequestStatusBadge } from '../components/Badges';
import RatingPrompt from '../components/RatingPrompt';
import {
  Truck, Package, Clock, CheckCircle, AlertTriangle, Plus, ArrowRight,
  Search, FileText, Bell, TrendingUp, MapPin, Map, BarChart3, Flame
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const DashboardPage = () => {
  const { user, profile, canPostLoads, canRequestLoads, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    myLoads: [],
    myRequests: [],
    assignedLoads: [],
    unreadNotifications: 0,
  });
  const [loading, setLoading] = useState(true);
  const [hotMarkets, setHotMarkets] = useState([]);
  const [marketSnapshot, setMarketSnapshot] = useState(null);

  useEffect(() => {
    loadDashboardData();
    loadMarketData();
  }, []);

  const loadMarketData = async () => {
    try {
      const [snapshotRes, regionsRes] = await Promise.all([
        api.get('/intel/market-snapshot', { params: { time_range: '24h' } }),
        api.get('/intel/regions', { params: { time_range: '24h' } })
      ]);
      setMarketSnapshot(snapshotRes.data);
      // Sort by load count and take top 5
      const sorted = [...(regionsRes.data || [])].sort((a, b) => b.load_count - a.load_count);
      setHotMarkets(sorted.slice(0, 5));
    } catch (error) {
      console.log('Market data not available');
    }
  };

  const loadDashboardData = async () => {
    try {
      const [notifCount] = await Promise.all([
        notificationsAPI.getUnreadCount(),
      ]);

      const newStats = {
        unreadNotifications: notifCount.data.unread_count,
        myLoads: [],
        myRequests: [],
        assignedLoads: [],
      };

      if (canPostLoads) {
        const loadsRes = await loadsAPI.getMyLoads();
        newStats.myLoads = loadsRes.data.slice(0, 5);
      }

      if (canRequestLoads) {
        const [requestsRes, assignedRes] = await Promise.all([
          loadRequestsAPI.getMyRequests(),
          loadRequestsAPI.getAssignedLoads(),
        ]);
        newStats.myRequests = requestsRes.data.slice(0, 5);
        newStats.assignedLoads = assignedRes.data.slice(0, 5);
      }

      setStats(newStats);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = canPostLoads
    ? [
        { icon: Plus, label: 'Post Load', href: '/post-load', primary: true },
        { icon: Search, label: 'View Requests', href: '/my-loads' },
        { icon: FileText, label: 'My Loads', href: '/my-loads' },
      ]
    : [
        { icon: Search, label: 'Find Loads', href: '/loadboard', primary: true },
        { icon: Package, label: 'My Requests', href: '/my-requests' },
        { icon: Truck, label: 'Assigned Loads', href: '/assigned-loads' },
      ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 pt-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-secondary/30 rounded-sm" />
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-40 bg-secondary/30 rounded-sm" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-20 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-8 animate-fade-in">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold uppercase font-['Barlow_Condensed'] tracking-tight text-[#0D1B2A]">
                Welcome back, {profile?.full_name?.split(' ')[0] || 'User'}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-gray-600 capitalize">
                  {user?.role?.replace('_', ' ')}
                </span>
                <VerificationBadge status={profile?.verification_status || 'pending'} />
              </div>
            </div>
            <div className="flex gap-3">
              {quickActions.map((action) => (
                <Link key={action.href} to={action.href}>
                  <Button
                    variant={action.primary ? 'default' : 'outline'}
                    className={action.primary ? 'bg-[#3B82F6] hover:bg-[#2563EB]' : 'border-gray-300 text-[#0D1B2A]'}
                    data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <action.icon className="w-4 h-4 mr-2" />
                    {action.label}
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {canPostLoads && (
            <>
              <Card className="border-gray-200 bg-white shadow-sm" data-testid="stats-posted-loads">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">Posted Loads</p>
                      <p className="text-3xl font-bold font-['Barlow_Condensed'] text-[#0D1B2A]">{stats.myLoads.length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-sm bg-blue-500/10 flex items-center justify-center">
                      <Package className="w-6 h-6 text-blue-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white shadow-sm" data-testid="stats-active-loads">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">Active Loads</p>
                      <p className="text-3xl font-bold font-['Barlow_Condensed'] text-[#0D1B2A]">
                        {stats.myLoads.filter(l => ['posted', 'requested', 'assigned'].includes(l.status)).length}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          {canRequestLoads && (
            <>
              <Card className="border-gray-200 bg-white shadow-sm" data-testid="stats-my-requests">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">My Requests</p>
                      <p className="text-3xl font-bold font-['Barlow_Condensed'] text-[#0D1B2A]">{stats.myRequests.length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-sm bg-amber-500/10 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white shadow-sm" data-testid="stats-assigned-loads">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">Assigned Loads</p>
                      <p className="text-3xl font-bold font-['Barlow_Condensed'] text-[#0D1B2A]">{stats.assignedLoads.length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-sm bg-purple-500/10 flex items-center justify-center">
                      <Truck className="w-6 h-6 text-purple-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          <Card className="border-gray-200 bg-white shadow-sm" data-testid="stats-notifications">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Notifications</p>
                  <p className="text-3xl font-bold font-['Barlow_Condensed'] text-[#0D1B2A]">{stats.unreadNotifications}</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-cyan-500/10 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-cyan-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Intel Map Widget */}
        <Card className="border-gray-200 bg-white shadow-sm mb-8 animate-fade-in" data-testid="intel-map-widget">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide text-lg text-[#0D1B2A]">
                  Hot Markets Today
                </CardTitle>
                <CardDescription>Top pickup locations in the last 24 hours</CardDescription>
              </div>
            </div>
            <Link to="/intel-map">
              <Button variant="outline" size="sm" className="border-[#3B82F6]/30 text-[#3B82F6] hover:bg-[#3B82F6]/10">
                <Map className="w-4 h-4 mr-2" />
                Open Intel Map
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {hotMarkets.length > 0 ? (
                hotMarkets.map((market, idx) => (
                  <Link
                    key={market.state || idx}
                    to={`/intel-map?region=${market.state}`}
                    className="group"
                  >
                    <div className={`relative p-4 rounded-lg border transition-all hover:scale-105 ${
                      idx === 0 ? 'bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/30 hover:border-red-500/50' :
                      idx === 1 ? 'bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/30 hover:border-orange-500/50' :
                      idx === 2 ? 'bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border-yellow-500/30 hover:border-yellow-500/50' :
                      'bg-gray-50 border-gray-200 hover:border-[#3B82F6]/30'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-2xl font-bold font-['Barlow_Condensed'] ${
                          idx === 0 ? 'text-red-500' :
                          idx === 1 ? 'text-orange-500' :
                          idx === 2 ? 'text-yellow-500' :
                          'text-gray-400'
                        }`}>
                          #{idx + 1}
                        </span>
                        <TrendingUp className={`w-4 h-4 ${
                          idx === 0 ? 'text-red-500' :
                          idx === 1 ? 'text-orange-500' :
                          idx === 2 ? 'text-yellow-500' :
                          'text-gray-400'
                        }`} />
                      </div>
                      <div className="font-semibold text-[#0D1B2A] group-hover:text-[#3B82F6] transition-colors">
                        {market.name || market.state}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {market.load_count} loads
                      </div>
                      <div className="text-sm text-green-600 font-medium mt-1">
                        ${market.avg_price?.toLocaleString() || '0'} avg
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-5 text-center py-8">
                  <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Market data loading...</p>
                  <Link to="/intel-map">
                    <Button variant="outline" className="mt-4">
                      View Intel Map
                    </Button>
                  </Link>
                </div>
              )}
            </div>
            {marketSnapshot && (
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between text-sm">
                <div className="flex items-center gap-6">
                  <div>
                    <span className="text-gray-500">Total Loads Today: </span>
                    <span className="text-[#0D1B2A] font-semibold">{marketSnapshot.loads_today || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Avg Rate: </span>
                    <span className="text-green-600 font-semibold">${marketSnapshot.avg_payout?.toLocaleString() || '0'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Hottest Region: </span>
                    <span className="text-[#3B82F6] font-semibold">{marketSnapshot.hottest_region || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Verification Banner */}
        {profile?.verification_status === 'pending' && (
          <Card className="border-amber-500/20 bg-amber-500/5 mb-8 animate-fade-in" data-testid="verification-banner">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="font-medium">Complete Your Verification</p>
                    <p className="text-sm text-muted-foreground">
                      Upload your documents to get verified and unlock all features
                    </p>
                  </div>
                </div>
                <Link to="/documents">
                  <Button variant="outline" className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10">
                    Upload Documents
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Loads / Requests */}
          {canPostLoads && (
            <Card className="border-gray-200 bg-white shadow-sm" data-testid="recent-loads-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide text-[#0D1B2A]">
                    Recent Loads
                  </CardTitle>
                  <CardDescription>Your recently posted loads</CardDescription>
                </div>
                <Link to="/my-loads">
                  <Button variant="ghost" size="sm">
                    View All
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {stats.myLoads.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No loads posted yet</p>
                    <Link to="/post-load">
                      <Button className="mt-4 bg-[#3B82F6] hover:bg-[#2563EB]" data-testid="post-first-load-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Post Your First Load
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stats.myLoads.map((load) => (
                      <Link
                        key={load.id}
                        to={`/loads/${load.id}`}
                        className="block p-3 rounded-sm bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-100"
                        data-testid={`load-item-${load.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="font-medium text-sm text-[#0D1B2A]">
                                {load.pickup_location} → {load.delivery_location}
                              </p>
                              <p className="text-xs text-gray-500">
                                ${load.price.toLocaleString()} • {load.vehicle_count} vehicle(s)
                              </p>
                            </div>
                          </div>
                          <LoadStatusBadge status={load.status} />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {canRequestLoads && (
            <>
              <Card className="border-gray-200 bg-white shadow-sm" data-testid="my-requests-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide text-[#0D1B2A]">
                      My Requests
                    </CardTitle>
                    <CardDescription>Loads you've requested to haul</CardDescription>
                  </div>
                  <Link to="/my-requests">
                    <Button variant="ghost" size="sm">
                      View All
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {stats.myRequests.length === 0 ? (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">No requests yet</p>
                      <Link to="/loadboard">
                        <Button className="mt-4 bg-[#3B82F6] hover:bg-[#2563EB]" data-testid="find-loads-btn">
                          <Search className="w-4 h-4 mr-2" />
                          Find Loads
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {stats.myRequests.map((request) => (
                        <Link
                          key={request.id}
                          to={`/loads/${request.load_id}`}
                          className="block p-3 rounded-sm bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-100"
                          data-testid={`request-item-${request.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              {request.load ? (
                                <>
                                  <p className="font-medium text-sm text-[#0D1B2A]">
                                    {request.load.pickup_location} → {request.load.delivery_location}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    ${request.load.price.toLocaleString()}
                                  </p>
                                </>
                              ) : (
                                <p className="font-medium text-sm text-[#0D1B2A]">Load #{request.load_id.slice(0, 8)}</p>
                              )}
                            </div>
                            <RequestStatusBadge status={request.status} />
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-gray-200 bg-white shadow-sm" data-testid="assigned-loads-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide text-[#0D1B2A]">
                      Assigned Loads
                    </CardTitle>
                    <CardDescription>Loads assigned to you</CardDescription>
                  </div>
                  <Link to="/assigned-loads">
                    <Button variant="ghost" size="sm">
                      View All
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {stats.assignedLoads.length === 0 ? (
                    <div className="text-center py-8">
                      <Truck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">No assigned loads</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {stats.assignedLoads.map((load) => (
                        <Link
                          key={load.id}
                          to={`/loads/${load.id}`}
                          className="block p-3 rounded-sm bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-100"
                          data-testid={`assigned-item-${load.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Truck className="w-4 h-4 text-[#3B82F6]" />
                              <div>
                                <p className="font-medium text-sm text-[#0D1B2A]">
                                  {load.pickup_location} → {load.delivery_location}
                                </p>
                                <p className="text-xs text-gray-500">
                                  ${load.price.toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <LoadStatusBadge status={load.status} />
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Admin Link */}
        {isAdmin && (
          <Card className="border-[#3B82F6]/20 bg-[#3B82F6]/5 mt-6 animate-fade-in">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#3B82F6]" />
                  <div>
                    <p className="font-medium text-[#0D1B2A]">Admin Panel</p>
                    <p className="text-sm text-gray-500">
                      Manage users, documents, and platform settings
                    </p>
                  </div>
                </div>
                <Link to="/admin">
                  <Button className="bg-[#3B82F6] hover:bg-[#2563EB]" data-testid="admin-panel-btn">
                    Open Admin Panel
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Post FAB for Brokers */}
      {canPostLoads && (
        <button
          onClick={() => navigate('/post-load')}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-[#3B82F6] to-[#2563EB] rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center z-50"
          style={{boxShadow: '0 6px 24px rgba(59, 130, 246, 0.4)'}}
          data-testid="quick-post-fab"
          title="Quick Post Load"
        >
          <Plus className="w-7 h-7 text-white" />
        </button>
      )}

      {/* Rating Prompt for completed loads */}
      <RatingPrompt />
    </div>
  );
};

export default DashboardPage;
